import { useDispatch, useSelector } from "react-redux";
import ExitSurveyModal from "./ExitSurveyModal";
import { getApplicationRoleType, getCurrentUserId, getLoanId, getLoanPurpose, getNhApplicationPath, getNhDealApplicationId, getUserRole, setLogout } from "features/authSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useExitIntentHandler } from "hooks/useExitIntentHandler";
import { useNavigate } from "react-router-dom";
import {
  CreateSurveyDto,
  Feedback,
  Survey,
  SurveyType,
  useCreateSurveyMutation,
  useGetSurveyDetailsQuery,
  useUpdateSurveyDisplayedCountMutation,
  useUpdateSurveyMutation,
} from "../surveyService";
import { EXIT_SURVEY_LIMIT } from "utilities/Constant";
import { useGetloanOfficerQuery } from "pages/application/applicationService";
import { getLoanOfficerDomainName } from "services/common/loanOfficerSlice";
import { trackFSExitIntentCancelled, trackFSExitIntentCompleted, trackFSExitIntentDisplayed } from "utilities/full-story";
import { ExitIntentFsTrigger, getExitIntentFsTrigger, isExitSurveyEligibleStep } from "./isExitSurveyEligibleStep";
import { deleteAllFiles } from "utilities/files";

export default function ExitSurvey({ stepCode, stepCodeName }: { stepCode: string; stepCodeName?: string }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loanId = useSelector(getLoanId);
  const exitSurveySessionkey = `exitSurveyDisplayed_${loanId}`;
  const [showExitSurvey, setShowExitSurvey] = useState(false);
  const exitSurveyDisplayedForSession = sessionStorage.getItem(exitSurveySessionkey) === "true";
  const userRole = useSelector(getUserRole);
  const path = useSelector(getNhApplicationPath);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanPurpose = useSelector(getLoanPurpose);
  const loanOfficerDomainName = useSelector(getLoanOfficerDomainName);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [createSurvey, { isLoading, data: createdSurvey }] = useCreateSurveyMutation();
  const [updateSurvey] = useUpdateSurveyMutation();
  const [updateSurveyDisplayedCount] = useUpdateSurveyDisplayedCountMutation();
  const { data: surveyDetails, isSuccess: isSurveyDetailsSuccess } = useGetSurveyDetailsQuery({
    userId,
    nhDealApplicationId,
    surveyType: SurveyType.EXIT,
  });
  const { data: loanOfficerDetails } = useGetloanOfficerQuery(nhDealApplicationId, { skip: !nhDealApplicationId });

  const activeSurvey = createdSurvey ?? survey;

  const salesChannel = useMemo(() => {
    const domainName = loanOfficerDomainName || (loanOfficerDetails?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return undefined;
  }, [loanOfficerDomainName, loanOfficerDetails]);

  const getBorrowerType = useMemo(() => {
    if (applicationRoleType?.toLowerCase() === "primaryborrower") {
      return userRole?.toLowerCase() === "borrower" ? "primary" : "spouse";
    } else if (applicationRoleType?.toLowerCase() === "nonspousalcoborrower") {
      return userRole?.toLowerCase() === "borrower" ? "nscb" : "spouse";
    }
    return "unknown";
  }, [applicationRoleType, userRole]);

  const isSurveyDisplayableStepCode = useMemo(
    () => isExitSurveyEligibleStep(getBorrowerType, path, stepCode),
    [getBorrowerType, path, stepCode]
  );

  const isSurveyDisplayable = useMemo(() => {
    return (
      isSurveyDetailsSuccess &&
      (!survey || survey?.displayedCount < EXIT_SURVEY_LIMIT) &&
      isSurveyDisplayableStepCode &&
      !exitSurveyDisplayedForSession
    );
  }, [isSurveyDetailsSuccess, survey, isSurveyDisplayableStepCode, exitSurveyDisplayedForSession]);

  const { exitIntent, triggerPoint, nextUrl, pendingLogout } = useExitIntentHandler(exitSurveySessionkey, isSurveyDisplayable);
  const fsTrigger: ExitIntentFsTrigger | null = getExitIntentFsTrigger(triggerPoint);

  const setSurveyDisplayedForSession = () => {
    sessionStorage.setItem(exitSurveySessionkey, "true");
  };

  const completePendingNavigation = () => {
    setSurveyDisplayedForSession();
    setShowExitSurvey(false);
    if (!nextUrl) return;
    if (pendingLogout) {
      void deleteAllFiles();
      dispatch(setLogout());
    }
    navigate(nextUrl, { replace: true, state: { previousPath: location.pathname } });
  };

  const submitExitSurvey = (feedback: Feedback[]) => {
    trackFSExitIntentCompleted({
      loanPurpose,
      salesChannel,
      stepCode,
      stepCodeName,
      question1: feedback[0]?.answer,
      question2: feedback[1]?.answer,
      trigger: fsTrigger,
    });
    void triggerSubmitSurvey(feedback);
    completePendingNavigation();
  };

  const closeExitSurvey = (partialFeedback: Feedback[]) => {
    trackFSExitIntentCancelled({
      loanPurpose,
      salesChannel,
      stepCode,
      stepCodeName,
      question1: partialFeedback[0]?.answer,
      question2: partialFeedback[1]?.answer,
      trigger: fsTrigger,
    });
    void triggerUpdateSurveyDisplayedCount();
    completePendingNavigation();
  };

  const triggerCreateSurvey = useCallback(async () => {
    if (!triggerPoint) return;

    const surveyData: CreateSurveyDto = {
      type: SurveyType.EXIT,
      triggerPoint,
      Device: navigator.userAgent || "Unknown",
    };

    try {
      const created = await createSurvey({ userId, nhDealApplicationId, surveyData }).unwrap();
      if (created) setSurvey((current) => (current?.id === created.id ? current : created));
    } catch {
      console.error("Failed to create exit survey");
    }
  }, [createSurvey, nhDealApplicationId, triggerPoint, userId]);

  const triggerUpdateSurveyDisplayedCount = async () => {
    const surveyId = activeSurvey?.id;
    if (!surveyId) return;
    await updateSurveyDisplayedCount({ surveyId });
  };

  const triggerSubmitSurvey = async (feedback: Feedback[]) => {
    if (!activeSurvey?.id) return;
    await updateSurvey({ surveyId: activeSurvey.id, surveyData: { ...activeSurvey, feedbacks: feedback } });
  };

  useEffect(() => {
    if (!surveyDetails) return;
    setSurvey((current) => (current?.id === surveyDetails.id ? current : surveyDetails));
  }, [surveyDetails]);

  useEffect(() => {
    if (!createdSurvey) return;
    setSurvey((current) => (current?.id === createdSurvey.id ? current : createdSurvey));
  }, [createdSurvey]);

  useEffect(() => {
    if (!isSurveyDisplayable || !exitIntent) return;
    if (activeSurvey || surveyDetails) return;
    void triggerCreateSurvey();
  }, [activeSurvey, exitIntent, isSurveyDisplayable, surveyDetails, triggerCreateSurvey]);

  useEffect(() => {
    if (!isSurveyDisplayable || !exitIntent || !triggerPoint) return;
    trackFSExitIntentDisplayed({
      loanPurpose,
      salesChannel,
      stepCode,
      stepCodeName,
      trigger: getExitIntentFsTrigger(triggerPoint),
    });
    setShowExitSurvey(true);
  }, [isSurveyDisplayable, exitIntent, triggerPoint]);

  return showExitSurvey ? <ExitSurveyModal closeSurvey={closeExitSurvey} submitSurvey={submitExitSurvey} isLoading={isLoading} /> : null;
}
